import { Brain, ChevronDown, ChevronRight, Wrench } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { MarkdownRenderer } from '@/components/markdown';
import type { AgentChatActivityEvent } from '@/services/agents/chat-stream';

import { ThinkingRow, formatElapsed, progressLabel } from './thinking-row';
import { type TurnSegment, activityEvents } from './turn-timeline';

interface ActivityFeedProps {
  timeline: TurnSegment[];
  streaming: boolean;
  startedAt?: number;
}

type ThinkingSegment = Extract<TurnSegment, { kind: 'thinking' }>;

const TEXT_SEGMENT_CLASS =
  'min-w-0 flex-1 space-y-2 text-sm leading-5 text-foreground/90 [overflow-wrap:anywhere] [&>:first-child]:mt-0 [&>:last-child]:mb-0';

// Within this many pixels of the bottom counts as following the stream.
const STICK_TO_BOTTOM_PX = 24;

function useElapsedSeconds(since: number | undefined, active: boolean) {
  const [elapsed, setElapsed] = useState(() =>
    since ? Math.max(0, Math.round((Date.now() - since) / 1000)) : 0,
  );

  useEffect(() => {
    if (!since || !active) return;
    const tick = () =>
      setElapsed(Math.max(0, Math.round((Date.now() - since) / 1000)));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [since, active]);

  return elapsed;
}

// Each result is paired with the nearest preceding unmatched call of the same
// tool name.
function computeToolDurations(
  events: AgentChatActivityEvent[],
): Map<AgentChatActivityEvent, number> {
  const durations = new Map<AgentChatActivityEvent, number>();
  const pending = new Map<string, AgentChatActivityEvent[]>();
  events.forEach((e) => {
    const name = e.toolName || 'tool';
    if (e.type === 'tool_call') {
      pending.set(name, [...(pending.get(name) ?? []), e]);
    } else if (e.type === 'tool_result') {
      const call = pending.get(name)?.pop();
      if (call?.at && e.at && e.at >= call.at) {
        durations.set(e, Math.round((e.at - call.at) / 1000));
      }
    }
  });
  return durations;
}

function thinkingLabel(segment: ThinkingSegment, live: boolean): string {
  if (live) return 'Thinking…';
  if (segment.startedAt && segment.endedAt !== undefined) {
    const seconds = Math.max(
      1,
      Math.round((segment.endedAt - segment.startedAt) / 1000),
    );
    return `Thought for ${formatElapsed(seconds)}`;
  }
  return 'Thought';
}

function stepsSummary(segments: TurnSegment[]): string {
  const thinking = segments.filter(
    (s): s is ThinkingSegment => s.kind === 'thinking',
  );
  const thoughtMs = thinking.reduce(
    (sum, s) =>
      s.startedAt && s.endedAt !== undefined
        ? sum + (s.endedAt - s.startedAt)
        : sum,
    0,
  );
  const toolCalls = segments.filter(
    (s) => s.kind === 'activity' && s.event.type === 'tool_call',
  ).length;
  const parts: string[] = [];
  if (thinking.length) {
    parts.push(
      thoughtMs > 0
        ? `Thought for ${formatElapsed(Math.max(1, Math.round(thoughtMs / 1000)))}`
        : 'Thought',
    );
  }
  if (toolCalls) {
    parts.push(`${toolCalls} tool call${toolCalls === 1 ? '' : 's'}`);
  }
  return parts.join(' · ') || 'Worked';
}

function ThinkingBlock({
  segment,
  live,
}: {
  segment: ThinkingSegment;
  live: boolean;
}) {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className='min-w-0 flex-1'>
      <button
        type='button'
        data-testid='activity-feed-thinking-toggle'
        className='flex items-center gap-1 italic text-muted-foreground transition-colors hover:text-foreground'
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className={live ? 'animate-pulse' : undefined}>
          {thinkingLabel(segment, live)}
        </span>
        {expanded ? (
          <ChevronDown className='size-3 shrink-0' />
        ) : (
          <ChevronRight className='size-3 shrink-0' />
        )}
      </button>
      {expanded && (
        <p
          data-testid='activity-feed-thinking'
          className='mt-1 whitespace-pre-wrap italic text-muted-foreground [overflow-wrap:anywhere]'
        >
          {segment.text}
        </p>
      )}
    </div>
  );
}

// Enough to read a tool's arguments or a result's shape without a runaway
// payload blowing up the feed.
const TOOL_DETAIL_CHARS = 2000;

function ActivityRow({
  event,
  running,
  duration,
}: {
  event: AgentChatActivityEvent;
  running: boolean;
  duration?: number;
}) {
  const [expanded, setExpanded] = useState(false);

  if (event.type === 'status') {
    return (
      <span className='min-w-0 break-words'>{event.text || 'Working…'}</span>
    );
  }

  const isResult = event.type === 'tool_result';
  const details = event.text ?? '';

  return (
    <div className='min-w-0 flex-1'>
      <button
        type='button'
        data-testid='activity-feed-tool-toggle'
        className='flex items-center gap-1 text-left transition-colors hover:text-foreground disabled:cursor-default'
        aria-expanded={details ? expanded : undefined}
        disabled={!details}
        onClick={() => setExpanded((v) => !v)}
      >
        <span className='font-medium text-foreground/80'>
          {event.toolName || 'tool'}
          {isResult && ' →'}
        </span>
        {running && <span className='text-[10px] opacity-70'>running…</span>}
        {isResult && duration !== undefined && (
          <span className='text-[10px] tabular-nums opacity-70'>
            {formatElapsed(duration)}
          </span>
        )}
        {details &&
          (expanded ? (
            <ChevronDown className='size-3 shrink-0' />
          ) : (
            <ChevronRight className='size-3 shrink-0' />
          ))}
      </button>
      {expanded && details && (
        <pre
          data-testid='activity-feed-tool-details'
          className='subtle-scrollbar mt-1 max-h-40 overflow-auto whitespace-pre-wrap font-mono text-[10px] [overflow-wrap:anywhere]'
        >
          {details.length > TOOL_DETAIL_CHARS
            ? `${details.slice(0, TOOL_DETAIL_CHARS)}…`
            : details}
        </pre>
      )}
    </div>
  );
}

function SegmentIcon({ segment }: { segment: TurnSegment }) {
  if (segment.kind === 'thinking') {
    return <Brain className='size-3 mt-0.5 shrink-0' />;
  }
  if (
    segment.kind === 'activity' &&
    (segment.event.type === 'tool_call' || segment.event.type === 'tool_result')
  ) {
    return <Wrench className='size-3 mt-0.5 shrink-0' />;
  }
  return (
    <span className='mt-1 h-1.5 w-1.5 rounded-full bg-muted-foreground/60 shrink-0' />
  );
}

export function ActivityFeed({
  timeline,
  streaming,
  startedAt,
}: ActivityFeedProps) {
  // Collapsed by default, streaming or not: the one-line header is all most
  // readers need, and the final answer never depends on this state.
  const [expanded, setExpanded] = useState(false);
  const turnElapsed = useElapsedSeconds(startedAt, streaming);

  const events = useMemo(() => activityEvents(timeline), [timeline]);
  const segments = timeline;
  const toolDurations = useMemo(() => computeToolDurations(events), [events]);

  const lastIndex = segments.length - 1;
  const lastSegment = segments[lastIndex];

  const summary = stepsSummary(timeline);

  const listRef = useRef<HTMLUListElement>(null);
  const followingRef = useRef(true);

  useEffect(() => {
    const list = listRef.current;
    if (!list || !streaming || !followingRef.current) return;
    list.scrollTop = list.scrollHeight;
  }, [segments, streaming, expanded]);

  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    followingRef.current =
      list.scrollHeight - list.scrollTop - list.clientHeight <=
      STICK_TO_BOTTOM_PX;
  };

  return (
    <div data-testid='activity-feed' className='text-xs'>
      <button
        type='button'
        className='flex w-full items-center gap-2 text-left text-muted-foreground transition-colors hover:text-foreground'
        aria-expanded={expanded}
        onClick={() => setExpanded((v) => !v)}
      >
        {streaming ? (
          <ThinkingRow
            label={progressLabel(events)}
            elapsedSeconds={startedAt !== undefined ? turnElapsed : undefined}
          />
        ) : (
          <span className='min-w-0 flex-1 truncate'>{summary}</span>
        )}
        {expanded ? (
          <ChevronDown className='size-3.5 shrink-0' />
        ) : (
          <ChevronRight className='size-3.5 shrink-0' />
        )}
      </button>
      {expanded && (
        <ul
          ref={listRef}
          onScroll={handleScroll}
          data-testid='activity-feed-events'
          className='subtle-scrollbar mt-2 max-h-96 space-y-1.5 overflow-y-auto border-l border-border/60 pl-3'
        >
          {segments.map((segment, i) => (
            <li
              key={`${segment.kind}-${i}`}
              data-segment={segment.kind}
              className='flex items-start gap-1.5 text-muted-foreground'
            >
              <SegmentIcon segment={segment} />
              {segment.kind === 'thinking' && (
                <ThinkingBlock
                  segment={segment}
                  live={
                    streaming &&
                    i === lastIndex &&
                    segment.endedAt === undefined
                  }
                />
              )}
              {segment.kind === 'text' && (
                <MarkdownRenderer
                  content={segment.text}
                  className={TEXT_SEGMENT_CLASS}
                />
              )}
              {segment.kind === 'activity' && (
                <ActivityRow
                  event={segment.event}
                  running={
                    streaming &&
                    segment === lastSegment &&
                    segment.event.type === 'tool_call'
                  }
                  duration={toolDurations.get(segment.event)}
                />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
